import * as THREE from 'three';
import { lerp } from './util.js';
import { WATER_LEVEL } from './layout.js';

const vert = /* glsl */ `
  varying vec3 vWorld;
  #include <fog_pars_vertex>
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorld = wp.xyz;
    vec4 mvPosition = viewMatrix * wp;
    gl_Position = projectionMatrix * mvPosition;
    #include <fog_vertex>
  }
`;

const frag = /* glsl */ `
  uniform float uTime, uNight, uLight;
  uniform sampler2D uHeight;
  uniform vec2 uCenter;
  uniform float uSize;
  uniform vec3 uSunDir, uSunColor, uShallow, uDeep, uHorizon, uZenith;
  varying vec3 vWorld;
  #include <fog_pars_fragment>

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y);
  }
  float wave(vec2 p) {
    float t = uTime;
    return vnoise(p * 0.8 + vec2(t * 0.30, t * 0.18)) * 0.6 + vnoise(p * 1.9 + vec2(-t * 0.22, t * 0.33)) * 0.4;
  }

  void main() {
    vec2 uv = (vWorld.xz - uCenter) / uSize + 0.5;
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) discard;
    float g = texture2D(uHeight, uv).r * 3.0 - 1.5;
    float depth = max(-g, 0.0);
    if (depth <= 0.0 && g > 0.25) discard;

    vec2 p = vWorld.xz;
    float e = 0.12;
    float w0 = wave(p);
    vec3 n = normalize(vec3(-(wave(p + vec2(e, 0.0)) - w0) / e * 0.22, 1.0, -(wave(p + vec2(0.0, e)) - w0) / e * 0.22));
    vec3 V = normalize(cameraPosition - vWorld);
    float fres = pow(1.0 - max(dot(n, V), 0.0), 3.0);

    vec3 base = mix(uShallow, uDeep, smoothstep(0.0, 1.3, depth));
    vec3 refl = mix(uHorizon, uZenith, 0.4);
    vec3 col = mix(base, refl, clamp(fres * 0.75 + 0.05, 0.0, 1.0));

    // soft flowing light streaks
    float streak = smoothstep(0.66, 0.78, vnoise(p * vec2(0.6, 2.4) + vec2(uTime * 0.5, uTime * 0.1)));
    col += uSunColor * streak * 0.10 * (1.0 - uNight);

    // sun glint
    vec3 R = reflect(-V, n);
    float sp = pow(max(dot(R, uSunDir), 0.0), 140.0);
    col += uSunColor * sp * 2.4 * (1.0 - uNight * 0.7);

    // shoreline foam + expanding ripple rings
    float jig = (vnoise(p * 2.3 + uTime * 0.45) - 0.5) * 0.14;
    float foam = 1.0 - smoothstep(0.02, 0.2, depth + jig);
    float ring = smoothstep(0.82, 1.0, sin(depth * 10.0 - uTime * 1.3 + vnoise(p * 1.1) * 3.0) * 0.5 + 0.5);
    ring *= (1.0 - smoothstep(0.15, 0.62, depth)) * 0.55;
    float foamAmt = clamp(max(foam, ring), 0.0, 1.0);
    col = mix(col, vec3(1.0), foamAmt * 0.92);

    col *= uLight;
    float alpha = mix(0.5, 0.96, smoothstep(0.0, 0.7, depth));
    alpha = max(alpha, foamAmt);
    alpha *= smoothstep(0.0, 0.04, depth + 0.06);

    gl_FragColor = vec4(col, alpha);
    #include <fog_fragment>
  }
`;

export function createWater(heightInfo) {
  const uniforms = THREE.UniformsUtils.merge([
    THREE.UniformsLib.fog,
    {
      uTime: { value: 0 },
      uNight: { value: 0 },
      uLight: { value: 1 },
      uHeight: { value: heightInfo.texture },
      uCenter: { value: heightInfo.center },
      uSize: { value: heightInfo.size },
      uSunDir: { value: new THREE.Vector3(0, 1, 0) },
      uSunColor: { value: new THREE.Color('#fff') },
      uShallow: { value: new THREE.Color('#78e3cf') },
      uDeep: { value: new THREE.Color('#2a8bd0') },
      uHorizon: { value: new THREE.Color() },
      uZenith: { value: new THREE.Color() },
    },
  ]);
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: vert,
    fragmentShader: frag,
    transparent: true,
    depthWrite: false,
    fog: true,
  });
  const geo = new THREE.PlaneGeometry(heightInfo.size, heightInfo.size, 1, 1).rotateX(-Math.PI / 2);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(heightInfo.center.x, WATER_LEVEL, heightInfo.center.y);
  mesh.renderOrder = 2;
  mesh.name = 'water';

  return {
    mesh,
    update(time, sky) {
      uniforms.uTime.value = time;
      uniforms.uNight.value = sky.night;
      uniforms.uSunDir.value.copy(sky.dayW > 0.05 ? sky.sunDir : sky.moonDir);
      uniforms.uSunColor.value.copy(sky.dayW > 0.05 ? sky.sunColor : new THREE.Color('#b8c8ff'));
      uniforms.uHorizon.value.copy(sky.horizon);
      uniforms.uZenith.value.copy(sky.zenith);
      uniforms.uLight.value = lerp(0.3, 1.0, sky.dayW) * lerp(1, 0.9, sky.night);
    },
  };
}
